import { CHILD_CHARACTER_ULTIMATE_SOURCE } from '../../constants/mvmChildUltimateResolution'

const PUBLIC_URL = process.env.PUBLIC_URL || ''

/** DOM typography for hex / MvM battle UI (`HexBattlePage` root + HUD). Georgia stack site-wide on this route. */
export const MVM_BATTLE_UI_FONT_FAMILY = 'Georgia, "Times New Roman", Times, serif'

/** MvM / hex HUD webp art on Vercel Blob — filenames match former `public/assets/battle/`. */
export const MVM_HUD_BLOB_BASE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm/new'
export function mvmHudBlobUrl(filename: string): string {
  return `${MVM_HUD_BLOB_BASE}/${filename.replace(/^\//, '')}`
}

/** Land + world-map HUD — opens alliance battle slide panel. */
export const MVM_ALLIANCE_BATTLE_HUD_ICON_URL = mvmHudBlobUrl('alliance-battle.webp')

/** Vercel Blob — hex battle atlases: `…/animations/mvm-characters/{name}/{file}.json` + PNG (`meta.image`). */
export const MVM_CHARACTER_ANIMATIONS_BASE =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/animations/mvm-characters'

export function mvmCharacterAnimationsBase(characterFolder: string): string {
  return `${MVM_CHARACTER_ANIMATIONS_BASE}/${characterFolder.trim().toLowerCase()}`
}

/** Sakura = default folder. (`sheetBase` on an asset entry still lets a character reuse another's atlas PNG if needed.) */
export const SAKURA_ASSETS_BASE = mvmCharacterAnimationsBase('sakura')
export const RENA_ASSETS_BASE = mvmCharacterAnimationsBase('rena')
export const HAYATE_ASSETS_BASE = mvmCharacterAnimationsBase('hayate')
export const MOTOKO_ASSETS_BASE = mvmCharacterAnimationsBase('motoko')
export const CHAR_URL = `${PUBLIC_URL}/assets/battle/hex-character.webp`

/** MvM elemental trap animations (TexturePacker JSON + PNG in same folder). */
export const MVM_ELEMENTAL_SHEETS_BASE = `${PUBLIC_URL}/assets/battle`
export const MVM_ELEMENTAL_FIRE_JSON = `${PUBLIC_URL}/assets/battle/fire.json`
export const MVM_ELEMENTAL_WATER_JSON = `${PUBLIC_URL}/assets/battle/water.json`
export const MVM_ELEMENTAL_LIGHTNING_JSON = `${PUBLIC_URL}/assets/battle/lightning.json`
export const MVM_ELEMENTAL_WOOD_TRAP_URL = `${PUBLIC_URL}/assets/battle/wood-trap.webp`

/**
 * Hit-burst spritesheets played on every damage hit (random pick between variants).
 * JSON lives in /public so it can ship with the bundle; PNG ships from Vercel Blob.
 * The loader fetches JSON locally and resolves `meta.image` against `MVM_HIT_BURST_PNG_BASE`.
 */
export const MVM_HIT_BURST_PNG_BASE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm'
export const MVM_HIT_BURST_JSONS = [
  `${PUBLIC_URL}/assets/battle/hit-1.json`,
  `${PUBLIC_URL}/assets/battle/hit-2.json`,
]
/**
 * Ghost-rising puff played once when a character dies (JSON in /public, PNG on Vercel Blob —
 * reuses MVM_HIT_BURST_PNG_BASE because they share a host).
 */
export const MVM_GHOST_PUFF_JSON = `${PUBLIC_URL}/assets/battle/ghost-buff.json`

/**
 * Status-effect icon PNGs (no JSON — single-image icons). Hosted on Vercel Blob like the rest
 * of the MvM battle FX. Map from server `statusEffects[].effect` name → icon URL is in
 * `mvmStatusIconForEffect` in mvmElementalPixiFx.ts.
 */
export const MVM_STATUS_ICON_BASE = MVM_HIT_BURST_PNG_BASE
export const MVM_STATUS_ICON_URLS = {
  sleep: `${MVM_STATUS_ICON_BASE}/sleep.png`,
  dot: `${MVM_STATUS_ICON_BASE}/dot.png`,
  armor_buff: `${MVM_STATUS_ICON_BASE}/armor_buff.png`,
  armor_debuff: `${MVM_STATUS_ICON_BASE}/armor_debuff.png`,
  attack_buff: `${MVM_STATUS_ICON_BASE}/attack_buff.png`,
  attack_debuff: `${MVM_STATUS_ICON_BASE}/attack_debuff.png`,
  lifesteal: `${MVM_STATUS_ICON_BASE}/lifesteal.png`,
  reflect: `${MVM_STATUS_ICON_BASE}/reflect.png`,
} as const
export type MvmStatusIconKey = keyof typeof MVM_STATUS_ICON_URLS

/** Sakura walk spritesheets: direction -> { json, png } */
export const SAKURA_WALK_ASSETS = {
  down: { json: `${SAKURA_ASSETS_BASE}/walk-down.json`, png: 'walk-down.png' },
  'down-left': { json: `${SAKURA_ASSETS_BASE}/walk-down-left.json`, png: 'walk-down-left.png' },
  'down-right': { json: `${SAKURA_ASSETS_BASE}/walk-down-right.json`, png: 'walk-down-right.png' },
  up: { json: `${SAKURA_ASSETS_BASE}/walk-up.json`, png: 'walk-up.png' },
  'up-left': { json: `${SAKURA_ASSETS_BASE}/walk-up-left.json`, png: 'walk-up-left.png' },
  'up-right': { json: `${SAKURA_ASSETS_BASE}/walk-up-right.json`, png: 'walk-up-right.png' },
} as const

/** Sakura attack spritesheets (6 directions) */
export const SAKURA_ATTACK_ASSETS = {
  down: { json: `${SAKURA_ASSETS_BASE}/sakura-attack-down.json`, png: 'sakura-attack-down.png' },
  'down-left': { json: `${SAKURA_ASSETS_BASE}/sakura-attack-down-left.json`, png: 'sakura-attack-down-left.png' },
  'down-right': { json: `${SAKURA_ASSETS_BASE}/sakura-attack-down-right.json`, png: 'sakura-attack-down-right.png' },
  up: { json: `${SAKURA_ASSETS_BASE}/sakura-attack-up.json`, png: 'sakura-attack-up.png' },
  'up-left': { json: `${SAKURA_ASSETS_BASE}/sakura-attack-up-left.json`, png: 'sakura-attack-up-left.png' },
  'up-right': { json: `${SAKURA_ASSETS_BASE}/sakura-attack-up-right.json`, png: 'sakura-attack-up-right.png' },
} as const

/** Sakura idle spritesheets: direction -> { json, png } */
export const SAKURA_IDLE_ASSETS = {
  down: { json: `${SAKURA_ASSETS_BASE}/idle-down.json`, png: 'idle-down.png' },
  'down-left': { json: `${SAKURA_ASSETS_BASE}/idle-down-left.json`, png: 'idle-down-left.png' },
  'down-right': { json: `${SAKURA_ASSETS_BASE}/idle-down-right.json`, png: 'idle-down-right.png' },
  up: { json: `${SAKURA_ASSETS_BASE}/idle-up.json`, png: 'idle-up.png' },
  'up-left': { json: `${SAKURA_ASSETS_BASE}/idle-up-left.json`, png: 'idle-up-left.png' },
  'up-right': { json: `${SAKURA_ASSETS_BASE}/idle-up-right.json`, png: 'idle-up-right.png' },
} as const

/** Rena walk spritesheets: direction -> { json, png } (`mvm-characters/rena/` on blob). */
export const RENA_WALK_ASSETS = {
  down: { json: `${RENA_ASSETS_BASE}/rena-walk-down.json`, png: 'rena-walk-down.png' },
  'down-left': { json: `${RENA_ASSETS_BASE}/rena-walk-down-left.json`, png: 'rena-walk-down-left.png' },
  'down-right': { json: `${RENA_ASSETS_BASE}/rena-walk-down-right.json`, png: 'rena-walk-down-right.png' },
  up: { json: `${RENA_ASSETS_BASE}/rena-walk-up.json`, png: 'rena-walk-up.png' },
  'up-left': { json: `${RENA_ASSETS_BASE}/rena-walk-up-left.json`, png: 'rena-walk-up-left.png' },
  'up-right': { json: `${RENA_ASSETS_BASE}/rena-walk-up-right.json`, png: 'rena-walk-up-right.png' },
} as const

/** Rena attack spritesheets (6 directions) */
export const RENA_ATTACK_ASSETS = {
  down: { json: `${RENA_ASSETS_BASE}/rena-attack-down.json`, png: 'rena-attack-down.png' },
  'down-left': { json: `${RENA_ASSETS_BASE}/rena-attack-down-left.json`, png: 'rena-attack-down-left.png' },
  'down-right': { json: `${RENA_ASSETS_BASE}/rena-attack-down-right.json`, png: 'rena-attack-down-right.png' },
  up: { json: `${RENA_ASSETS_BASE}/rena-attack-up.json`, png: 'rena-attack-up.png' },
  'up-left': { json: `${RENA_ASSETS_BASE}/rena-attack-up-left.json`, png: 'rena-attack-up-left.png' },
  'up-right': { json: `${RENA_ASSETS_BASE}/rena-attack-up-right.json`, png: 'rena-attack-up-right.png' },
} as const

/** Rena idle spritesheets: direction -> { json, png } */
export const RENA_IDLE_ASSETS = {
  down: { json: `${RENA_ASSETS_BASE}/rena-idle-down.json`, png: 'rena-idle-down.png' },
  'down-left': { json: `${RENA_ASSETS_BASE}/rena-idle-down-left.json`, png: 'rena-idle-down-left.png' },
  'down-right': { json: `${RENA_ASSETS_BASE}/rena-idle-down-right.json`, png: 'rena-idle-down-right.png' },
  up: { json: `${RENA_ASSETS_BASE}/rena-idle-up.json`, png: 'rena-idle-up.png' },
  'up-left': { json: `${RENA_ASSETS_BASE}/rena-idle-up-left.json`, png: 'rena-idle-up-left.png' },
  'up-right': { json: `${RENA_ASSETS_BASE}/rena-idle-up-right.json`, png: 'rena-idle-up-right.png' },
} as const

/** Hayate — same layout as Rena (`mvm-characters/hayate/`). */
export const HAYATE_WALK_ASSETS = {
  down: { json: `${HAYATE_ASSETS_BASE}/hayate-walk-down.json`, png: 'hayate-walk-down.png' },
  'down-left': { json: `${HAYATE_ASSETS_BASE}/hayate-walk-down-left.json`, png: 'hayate-walk-down-left.png' },
  'down-right': { json: `${HAYATE_ASSETS_BASE}/hayate-walk-down-right.json`, png: 'hayate-walk-down-right.png' },
  up: { json: `${HAYATE_ASSETS_BASE}/hayate-walk-up.json`, png: 'hayate-walk-up.png' },
  'up-left': { json: `${HAYATE_ASSETS_BASE}/hayate-walk-up-left.json`, png: 'hayate-walk-up-left.png' },
  'up-right': { json: `${HAYATE_ASSETS_BASE}/hayate-walk-up-right.json`, png: 'hayate-walk-up-right.png' },
} as const

export const HAYATE_ATTACK_ASSETS = {
  down: { json: `${HAYATE_ASSETS_BASE}/hayate-attack-down.json`, png: 'hayate-attack-down.png' },
  'down-left': { json: `${HAYATE_ASSETS_BASE}/hayate-attack-down-left.json`, png: 'hayate-attack-down-left.png' },
  'down-right': { json: `${HAYATE_ASSETS_BASE}/hayate-attack-down-right.json`, png: 'hayate-attack-down-right.png' },
  up: { json: `${HAYATE_ASSETS_BASE}/hayate-attack-up.json`, png: 'hayate-attack-up.png' },
  'up-left': { json: `${HAYATE_ASSETS_BASE}/hayate-attack-up-left.json`, png: 'hayate-attack-up-left.png' },
  'up-right': { json: `${HAYATE_ASSETS_BASE}/hayate-attack-up-right.json`, png: 'hayate-attack-up-right.png' },
} as const

export const HAYATE_IDLE_ASSETS = {
  down: { json: `${HAYATE_ASSETS_BASE}/hayate-idle-down.json`, png: 'hayate-idle-down.png' },
  'down-left': { json: `${HAYATE_ASSETS_BASE}/hayate-idle-down-left.json`, png: 'hayate-idle-down-left.png' },
  'down-right': { json: `${HAYATE_ASSETS_BASE}/hayate-idle-down-right.json`, png: 'hayate-idle-down-right.png' },
  up: { json: `${HAYATE_ASSETS_BASE}/hayate-idle-up.json`, png: 'hayate-idle-up.png' },
  'up-left': { json: `${HAYATE_ASSETS_BASE}/hayate-idle-up-left.json`, png: 'hayate-idle-up-left.png' },
  'up-right': { json: `${HAYATE_ASSETS_BASE}/hayate-idle-up-right.json`, png: 'hayate-idle-up-right.png' },
} as const

export const SOLVARION_ASSETS_BASE = mvmCharacterAnimationsBase('solvarion')

export const SOLVARION_WALK_ASSETS = {
  down: { json: `${SOLVARION_ASSETS_BASE}/solvarion-walk-down.json`, png: 'solvarion-walk-down.png' },
  'down-left': { json: `${SOLVARION_ASSETS_BASE}/solvarion-walk-down-left.json`, png: 'solvarion-walk-down-left.png' },
  'down-right': { json: `${SOLVARION_ASSETS_BASE}/solvarion-walk-down-right.json`, png: 'solvarion-walk-down-right.png' },
  up: { json: `${SOLVARION_ASSETS_BASE}/solvarion-walk-up.json`, png: 'solvarion-walk-up.png' },
  'up-left': { json: `${SOLVARION_ASSETS_BASE}/solvarion-walk-up-left.json`, png: 'solvarion-walk-up-left.png' },
  'up-right': { json: `${SOLVARION_ASSETS_BASE}/solvarion-walk-up-right.json`, png: 'solvarion-walk-up-right.png' },
} as const

export const SOLVARION_IDLE_ASSETS = {
  down: { json: `${SOLVARION_ASSETS_BASE}/solvarion-idle-down.json`, png: 'solvarion-idle-down.png' },
  'down-left': { json: `${SOLVARION_ASSETS_BASE}/solvarion-idle-down-left.json`, png: 'solvarion-idle-down-left.png' },
  'down-right': { json: `${SOLVARION_ASSETS_BASE}/solvarion-idle-down-right.json`, png: 'solvarion-idle-down-right.png' },
  up: { json: `${SOLVARION_ASSETS_BASE}/solvarion-idle-up.json`, png: 'solvarion-idle-up.png' },
  'up-left': { json: `${SOLVARION_ASSETS_BASE}/solvarion-idle-up-left.json`, png: 'solvarion-idle-up-left.png' },
  'up-right': { json: `${SOLVARION_ASSETS_BASE}/solvarion-idle-up-right.json`, png: 'solvarion-idle-up-right.png' },
} as const

export const SOLVARION_ATTACK_ASSETS = {
  down: { json: `${SOLVARION_ASSETS_BASE}/solvarion-attack-down.json`, png: 'solvarion-attack-down.png' },
  'down-left': { json: `${SOLVARION_ASSETS_BASE}/solvarion-attack-down-left.json`, png: 'solvarion-attack-down-left.png' },
  'down-right': { json: `${SOLVARION_ASSETS_BASE}/solvarion-attack-down-right.json`, png: 'solvarion-attack-down-right.png' },
  up: { json: `${SOLVARION_ASSETS_BASE}/solvarion-attack-up.json`, png: 'solvarion-attack-up.png' },
  'up-left': { json: `${SOLVARION_ASSETS_BASE}/solvarion-attack-up-left.json`, png: 'solvarion-attack-up-left.png' },
  'up-right': { json: `${SOLVARION_ASSETS_BASE}/solvarion-attack-up-right.json`, png: 'solvarion-attack-up-right.png' },
} as const

export const MOTOKO_WALK_ASSETS = {
  down: { json: `${MOTOKO_ASSETS_BASE}/motoko-walk-down.json`, png: 'motoko-walk-down.png' },
  'down-left': { json: `${MOTOKO_ASSETS_BASE}/motoko-walk-down-left.json`, png: 'motoko-walk-down-left.png' },
  'down-right': { json: `${MOTOKO_ASSETS_BASE}/motoko-walk-down-right.json`, png: 'motoko-walk-down-right.png' },
  up: { json: `${MOTOKO_ASSETS_BASE}/motoko-walk-up.json`, png: 'motoko-walk-up.png' },
  'up-left': { json: `${MOTOKO_ASSETS_BASE}/motoko-walk-up-left.json`, png: 'motoko-walk-up-left.png' },
  'up-right': { json: `${MOTOKO_ASSETS_BASE}/motoko-walk-up-right.json`, png: 'motoko-walk-up-right.png' },
} as const

export const MOTOKO_IDLE_ASSETS = {
  down: { json: `${MOTOKO_ASSETS_BASE}/motoko-idle-down.json`, png: 'motoko-idle-down.png' },
  'down-left': { json: `${MOTOKO_ASSETS_BASE}/motoko-idle-down-left.json`, png: 'motoko-idle-down-left.png' },
  'down-right': { json: `${MOTOKO_ASSETS_BASE}/motoko-idle-down-right.json`, png: 'motoko-idle-down-right.png' },
  up: { json: `${MOTOKO_ASSETS_BASE}/motoko-idle-up.json`, png: 'motoko-idle-up.png' },
  'up-left': { json: `${MOTOKO_ASSETS_BASE}/motoko-idle-up-left.json`, png: 'motoko-idle-up-left.png' },
  'up-right': { json: `${MOTOKO_ASSETS_BASE}/motoko-idle-up-right.json`, png: 'motoko-idle-up-right.png' },
} as const

export const MOTOKO_ATTACK_ASSETS = {
  down: { json: `${MOTOKO_ASSETS_BASE}/motoko-attack-down.json`, png: 'motoko-attack-down.png' },
  'down-left': { json: `${MOTOKO_ASSETS_BASE}/motoko-attack-down-left.json`, png: 'motoko-attack-down-left.png' },
  'down-right': { json: `${MOTOKO_ASSETS_BASE}/motoko-attack-down-right.json`, png: 'motoko-attack-down-right.png' },
  up: { json: `${MOTOKO_ASSETS_BASE}/motoko-attack-up.json`, png: 'motoko-attack-up.png' },
  'up-left': { json: `${MOTOKO_ASSETS_BASE}/motoko-attack-up-left.json`, png: 'motoko-attack-up-left.png' },
  'up-right': { json: `${MOTOKO_ASSETS_BASE}/motoko-attack-up-right.json`, png: 'motoko-attack-up-right.png' },
} as const

export const VALYRA_ASSETS_BASE = mvmCharacterAnimationsBase('valyra')

export const VALYRA_WALK_ASSETS = {
  down: { json: `${VALYRA_ASSETS_BASE}/valyra-walk-down.json`, png: 'valyra-walk-down.png' },
  'down-left': { json: `${VALYRA_ASSETS_BASE}/valyra-walk-down-left.json`, png: 'valyra-walk-down-left.png' },
  'down-right': { json: `${VALYRA_ASSETS_BASE}/valyra-walk-down-right.json`, png: 'valyra-walk-down-right.png' },
  up: { json: `${VALYRA_ASSETS_BASE}/valyra-walk-up.json`, png: 'valyra-walk-up.png' },
  'up-left': { json: `${VALYRA_ASSETS_BASE}/valyra-walk-up-left.json`, png: 'valyra-walk-up-left.png' },
  'up-right': { json: `${VALYRA_ASSETS_BASE}/valyra-walk-up-right.json`, png: 'valyra-walk-up-right.png' },
} as const

export const VALYRA_IDLE_ASSETS = {
  down: { json: `${VALYRA_ASSETS_BASE}/valyra-idle-down.json`, png: 'valyra-idle-down.png' },
  'down-left': { json: `${VALYRA_ASSETS_BASE}/valyra-idle-down-left.json`, png: 'valyra-idle-down-left.png' },
  'down-right': { json: `${VALYRA_ASSETS_BASE}/valyra-idle-down-right.json`, png: 'valyra-idle-down-right.png' },
  up: { json: `${VALYRA_ASSETS_BASE}/valyra-idle-up.json`, png: 'valyra-idle-up.png' },
  'up-left': { json: `${VALYRA_ASSETS_BASE}/valyra-idle-up-left.json`, png: 'valyra-idle-up-left.png' },
  'up-right': { json: `${VALYRA_ASSETS_BASE}/valyra-idle-up-right.json`, png: 'valyra-idle-up-right.png' },
} as const

export const VALYRA_ATTACK_ASSETS = {
  down: { json: `${VALYRA_ASSETS_BASE}/valyra-attack-down.json`, png: 'valyra-attack-down.png' },
  'down-left': { json: `${VALYRA_ASSETS_BASE}/valyra-attack-down-left.json`, png: 'valyra-attack-down-left.png' },
  'down-right': { json: `${VALYRA_ASSETS_BASE}/valyra-attack-down-right.json`, png: 'valyra-attack-down-right.png' },
  up: { json: `${VALYRA_ASSETS_BASE}/valyra-attack-up.json`, png: 'valyra-attack-up.png' },
  'up-left': { json: `${VALYRA_ASSETS_BASE}/valyra-attack-up-left.json`, png: 'valyra-attack-up-left.png' },
  'up-right': { json: `${VALYRA_ASSETS_BASE}/valyra-attack-up-right.json`, png: 'valyra-attack-up-right.png' },
} as const

export const BRUXUS_ASSETS_BASE = mvmCharacterAnimationsBase('bruxus')

export const BRUXUS_WALK_ASSETS = {
  down: { json: `${BRUXUS_ASSETS_BASE}/bruxus-walk-down.json`, png: 'bruxus-walk-down.png' },
  'down-left': { json: `${BRUXUS_ASSETS_BASE}/bruxus-walk-down-left.json`, png: 'bruxus-walk-down-left.png' },
  'down-right': { json: `${BRUXUS_ASSETS_BASE}/bruxus-walk-down-right.json`, png: 'bruxus-walk-down-right.png' },
  up: { json: `${BRUXUS_ASSETS_BASE}/bruxus-walk-up.json`, png: 'bruxus-walk-up.png' },
  'up-left': { json: `${BRUXUS_ASSETS_BASE}/bruxus-walk-up-left.json`, png: 'bruxus-walk-up-left.png' },
  'up-right': { json: `${BRUXUS_ASSETS_BASE}/bruxus-walk-up-right.json`, png: 'bruxus-walk-up-right.png' },
} as const

export const BRUXUS_IDLE_ASSETS = {
  down: { json: `${BRUXUS_ASSETS_BASE}/bruxus-idle-down.json`, png: 'bruxus-idle-down.png' },
  'down-left': { json: `${BRUXUS_ASSETS_BASE}/bruxus-idle-down-left.json`, png: 'bruxus-idle-down-left.png' },
  'down-right': { json: `${BRUXUS_ASSETS_BASE}/bruxus-idle-down-right.json`, png: 'bruxus-idle-down-right.png' },
  up: { json: `${BRUXUS_ASSETS_BASE}/bruxus-idle-up.json`, png: 'bruxus-idle-up.png' },
  'up-left': { json: `${BRUXUS_ASSETS_BASE}/bruxus-idle-up-left.json`, png: 'bruxus-idle-up-left.png' },
  'up-right': { json: `${BRUXUS_ASSETS_BASE}/bruxus-idle-up-right.json`, png: 'bruxus-idle-up-right.png' },
} as const

export const BRUXUS_ATTACK_ASSETS = {
  down: { json: `${BRUXUS_ASSETS_BASE}/bruxus-attack-down.json`, png: 'bruxus-attack-down.png' },
  'down-left': { json: `${BRUXUS_ASSETS_BASE}/bruxus-attack-down-left.json`, png: 'bruxus-attack-down-left.png' },
  'down-right': { json: `${BRUXUS_ASSETS_BASE}/bruxus-attack-down-right.json`, png: 'bruxus-attack-down-right.png' },
  up: { json: `${BRUXUS_ASSETS_BASE}/bruxus-attack-up.json`, png: 'bruxus-attack-up.png' },
  'up-left': { json: `${BRUXUS_ASSETS_BASE}/bruxus-attack-up-left.json`, png: 'bruxus-attack-up-left.png' },
  'up-right': { json: `${BRUXUS_ASSETS_BASE}/bruxus-attack-up-right.json`, png: 'bruxus-attack-up-right.png' },
} as const

export const FINZA_ASSETS_BASE = mvmCharacterAnimationsBase('finza')

export const FINZA_IDLE_ASSETS = {
  down: { json: `${FINZA_ASSETS_BASE}/finza-idle-down.json`, png: 'finza-idle-down.png' },
  'down-left': { json: `${FINZA_ASSETS_BASE}/finza-idle-down-left.json`, png: 'finza-idle-down-left.png' },
  'down-right': { json: `${FINZA_ASSETS_BASE}/finza-idle-down-right.json`, png: 'finza-idle-down-right.png' },
  up: { json: `${FINZA_ASSETS_BASE}/finza-idle-up.json`, png: 'finza-idle-up.png' },
  'up-left': { json: `${FINZA_ASSETS_BASE}/finza-idle-up-left.json`, png: 'finza-idle-up-left.png' },
  'up-right': { json: `${FINZA_ASSETS_BASE}/finza-idle-up-right.json`, png: 'finza-idle-up-right.png' },
} as const

export const FINZA_WALK_ASSETS = {
  down: { json: `${FINZA_ASSETS_BASE}/finza-walk-down.json`, png: 'finza-walk-down.png' },
  'down-left': { json: `${FINZA_ASSETS_BASE}/finza-walk-down-left.json`, png: 'finza-walk-down-left.png' },
  'down-right': { json: `${FINZA_ASSETS_BASE}/finza-walk-down-right.json`, png: 'finza-walk-down-right.png' },
  up: { json: `${FINZA_ASSETS_BASE}/finza-walk-up.json`, png: 'finza-walk-up.png' },
  'up-left': { json: `${FINZA_ASSETS_BASE}/finza-walk-up-left.json`, png: 'finza-walk-up-left.png' },
  'up-right': { json: `${FINZA_ASSETS_BASE}/finza-walk-up-right.json`, png: 'finza-walk-up-right.png' },
} as const

export const FINZA_ATTACK_ASSETS = {
  down: { json: `${FINZA_ASSETS_BASE}/finza-attack-down.json`, png: 'finza-attack-down.png' },
  'down-left': { json: `${FINZA_ASSETS_BASE}/finza-attack-down-left.json`, png: 'finza-attack-down-left.png' },
  'down-right': { json: `${FINZA_ASSETS_BASE}/finza-attack-down-right.json`, png: 'finza-attack-down-right.png' },
  up: { json: `${FINZA_ASSETS_BASE}/finza-attack-up.json`, png: 'finza-attack-up.png' },
  'up-left': { json: `${FINZA_ASSETS_BASE}/finza-attack-up-left.json`, png: 'finza-attack-up-left.png' },
  'up-right': { json: `${FINZA_ASSETS_BASE}/finza-attack-up-right.json`, png: 'finza-attack-up-right.png' },
} as const

export const VITALIS_ASSETS_BASE = mvmCharacterAnimationsBase('vitalis')

export const VITALIS_WALK_ASSETS = {
  down: { json: `${VITALIS_ASSETS_BASE}/vitalis-walk-down.json`, png: 'vitalis-walk-down.png' },
  'down-left': { json: `${VITALIS_ASSETS_BASE}/vitalis-walk-down-left.json`, png: 'vitalis-walk-down-left.png' },
  'down-right': { json: `${VITALIS_ASSETS_BASE}/vitalis-walk-down-right.json`, png: 'vitalis-walk-down-right.png' },
  up: { json: `${VITALIS_ASSETS_BASE}/vitalis-walk-up.json`, png: 'vitalis-walk-up.png' },
  'up-left': { json: `${VITALIS_ASSETS_BASE}/vitalis-walk-up-left.json`, png: 'vitalis-walk-up-left.png' },
  'up-right': { json: `${VITALIS_ASSETS_BASE}/vitalis-walk-up-right.json`, png: 'vitalis-walk-up-right.png' },
} as const

export const VITALIS_IDLE_ASSETS = {
  down: { json: `${VITALIS_ASSETS_BASE}/vitalis-idle-down.json`, png: 'vitalis-idle-down.png' },
  'down-left': { json: `${VITALIS_ASSETS_BASE}/vitalis-idle-down-left.json`, png: 'vitalis-idle-down-left.png' },
  'down-right': { json: `${VITALIS_ASSETS_BASE}/vitalis-idle-down-right.json`, png: 'vitalis-idle-down-right.png' },
  up: { json: `${VITALIS_ASSETS_BASE}/vitalis-idle-up.json`, png: 'vitalis-idle-up.png' },
  'up-left': { json: `${VITALIS_ASSETS_BASE}/vitalis-idle-up-left.json`, png: 'vitalis-idle-up-left.png' },
  'up-right': { json: `${VITALIS_ASSETS_BASE}/vitalis-idle-up-right.json`, png: 'vitalis-idle-up-right.png' },
} as const

export const VITALIS_ATTACK_ASSETS = {
  down: { json: `${VITALIS_ASSETS_BASE}/vitalis-attack-down.json`, png: 'vitalis-attack-down.png' },
  'down-left': { json: `${VITALIS_ASSETS_BASE}/vitalis-attack-down-left.json`, png: 'vitalis-attack-down-left.png' },
  'down-right': { json: `${VITALIS_ASSETS_BASE}/vitalis-attack-down-right.json`, png: 'vitalis-attack-down-right.png' },
  up: { json: `${VITALIS_ASSETS_BASE}/vitalis-attack-up.json`, png: 'vitalis-attack-up.png' },
  'up-left': { json: `${VITALIS_ASSETS_BASE}/vitalis-attack-up-left.json`, png: 'vitalis-attack-up-left.png' },
  'up-right': { json: `${VITALIS_ASSETS_BASE}/vitalis-attack-up-right.json`, png: 'vitalis-attack-up-right.png' },
} as const

export const BULA_ASSETS_BASE = mvmCharacterAnimationsBase('bula')

export const BULA_WALK_ASSETS = {
  down: { json: `${BULA_ASSETS_BASE}/bula-walk-down.json`, png: 'bula-walk-down.png' },
  'down-left': { json: `${BULA_ASSETS_BASE}/bula-walk-down-left.json`, png: 'bula-walk-down-left.png' },
  'down-right': { json: `${BULA_ASSETS_BASE}/bula-walk-down-right.json`, png: 'bula-walk-down-right.png' },
  up: { json: `${BULA_ASSETS_BASE}/bula-walk-up.json`, png: 'bula-walk-up.png' },
  'up-left': { json: `${BULA_ASSETS_BASE}/bula-walk-up-left.json`, png: 'bula-walk-up-left.png' },
  'up-right': { json: `${BULA_ASSETS_BASE}/bula-walk-up-right.json`, png: 'bula-walk-up-right.png' },
} as const

export const BULA_IDLE_ASSETS = {
  down: { json: `${BULA_ASSETS_BASE}/bula-idle-down.json`, png: 'bula-idle-down.png' },
  'down-left': { json: `${BULA_ASSETS_BASE}/bula-idle-down-left.json`, png: 'bula-idle-down-left.png' },
  'down-right': { json: `${BULA_ASSETS_BASE}/bula-idle-down-right.json`, png: 'bula-idle-down-right.png' },
  up: { json: `${BULA_ASSETS_BASE}/bula-idle-up.json`, png: 'bula-idle-up.png' },
  'up-left': { json: `${BULA_ASSETS_BASE}/bula-idle-up-left.json`, png: 'bula-idle-up-left.png' },
  'up-right': { json: `${BULA_ASSETS_BASE}/bula-idle-up-right.json`, png: 'bula-idle-up-right.png' },
} as const

export const BULA_ATTACK_ASSETS = {
  down: { json: `${BULA_ASSETS_BASE}/bula-attack-down.json`, png: 'bula-attack-down.png' },
  'down-left': { json: `${BULA_ASSETS_BASE}/bula-attack-down-left.json`, png: 'bula-attack-down-left.png' },
  'down-right': { json: `${BULA_ASSETS_BASE}/bula-attack-down-right.json`, png: 'bula-attack-down-right.png' },
  up: { json: `${BULA_ASSETS_BASE}/bula-attack-up.json`, png: 'bula-attack-up.png' },
  'up-left': { json: `${BULA_ASSETS_BASE}/bula-attack-up-left.json`, png: 'bula-attack-up-left.png' },
  'up-right': { json: `${BULA_ASSETS_BASE}/bula-attack-up-right.json`, png: 'bula-attack-up-right.png' },
} as const

export const CAELORIA_ASSETS_BASE = mvmCharacterAnimationsBase('caeloria')

export const CAELORIA_WALK_ASSETS = {
  down: { json: `${CAELORIA_ASSETS_BASE}/caeloria-walk-down.json`, png: 'caeloria-walk-down.png' },
  'down-left': { json: `${CAELORIA_ASSETS_BASE}/caeloria-walk-down-left.json`, png: 'caeloria-walk-down-left.png' },
  'down-right': { json: `${CAELORIA_ASSETS_BASE}/caeloria-walk-down-right.json`, png: 'caeloria-walk-down-right.png' },
  up: { json: `${CAELORIA_ASSETS_BASE}/caeloria-walk-up.json`, png: 'caeloria-walk-up.png' },
  'up-left': { json: `${CAELORIA_ASSETS_BASE}/caeloria-walk-up-left.json`, png: 'caeloria-walk-up-left.png' },
  'up-right': { json: `${CAELORIA_ASSETS_BASE}/caeloria-walk-up-right.json`, png: 'caeloria-walk-up-right.png' },
} as const

export const CAELORIA_IDLE_ASSETS = {
  down: { json: `${CAELORIA_ASSETS_BASE}/caeloria-idle-down.json`, png: 'caeloria-idle-down.png' },
  'down-left': { json: `${CAELORIA_ASSETS_BASE}/caeloria-idle-down-left.json`, png: 'caeloria-idle-down-left.png' },
  'down-right': { json: `${CAELORIA_ASSETS_BASE}/caeloria-idle-down-right.json`, png: 'caeloria-idle-down-right.png' },
  up: { json: `${CAELORIA_ASSETS_BASE}/caeloria-idle-up.json`, png: 'caeloria-idle-up.png' },
  'up-left': { json: `${CAELORIA_ASSETS_BASE}/caeloria-idle-up-left.json`, png: 'caeloria-idle-up-left.png' },
  'up-right': { json: `${CAELORIA_ASSETS_BASE}/caeloria-idle-up-right.json`, png: 'caeloria-idle-up-right.png' },
} as const

export const CAELORIA_ATTACK_ASSETS = {
  down: { json: `${CAELORIA_ASSETS_BASE}/caeloria-attack-down.json`, png: 'caeloria-attack-down.png' },
  'down-left': { json: `${CAELORIA_ASSETS_BASE}/caeloria-attack-down-left.json`, png: 'caeloria-attack-down-left.png' },
  'down-right': { json: `${CAELORIA_ASSETS_BASE}/caeloria-attack-down-right.json`, png: 'caeloria-attack-down-right.png' },
  up: { json: `${CAELORIA_ASSETS_BASE}/caeloria-attack-up.json`, png: 'caeloria-attack-up.png' },
  'up-left': { json: `${CAELORIA_ASSETS_BASE}/caeloria-attack-up-left.json`, png: 'caeloria-attack-up-left.png' },
  'up-right': { json: `${CAELORIA_ASSETS_BASE}/caeloria-attack-up-right.json`, png: 'caeloria-attack-up-right.png' },
} as const

export const NYTHERIS_ASSETS_BASE = mvmCharacterAnimationsBase('nytheris')

export const NYTHERIS_WALK_ASSETS = {
  down: { json: `${NYTHERIS_ASSETS_BASE}/nytheris-walk-down.json`, png: 'nytheris-walk-down.png' },
  'down-left': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-walk-down-left.json`, png: 'nytheris-walk-down-left.png' },
  'down-right': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-walk-down-right.json`, png: 'nytheris-walk-down-right.png' },
  up: { json: `${NYTHERIS_ASSETS_BASE}/nytheris-walk-up.json`, png: 'nytheris-walk-up.png' },
  'up-left': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-walk-up-left.json`, png: 'nytheris-walk-up-left.png' },
  'up-right': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-walk-up-right.json`, png: 'nytheris-walk-up-right.png' },
} as const

export const NYTHERIS_IDLE_ASSETS = {
  down: { json: `${NYTHERIS_ASSETS_BASE}/nytheris-idle-down.json`, png: 'nytheris-idle-down.png' },
  'down-left': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-idle-down-left.json`, png: 'nytheris-idle-down-left.png' },
  'down-right': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-idle-down-right.json`, png: 'nytheris-idle-down-right.png' },
  up: { json: `${NYTHERIS_ASSETS_BASE}/nytheris-idle-up.json`, png: 'nytheris-idle-up.png' },
  'up-left': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-idle-up-left.json`, png: 'nytheris-idle-up-left.png' },
  'up-right': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-idle-up-right.json`, png: 'nytheris-idle-up-right.png' },
} as const

export const NYTHERIS_ATTACK_ASSETS = {
  down: { json: `${NYTHERIS_ASSETS_BASE}/nytheris-attack-down.json`, png: 'nytheris-attack-down.png' },
  'down-left': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-attack-down-left.json`, png: 'nytheris-attack-down-left.png' },
  'down-right': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-attack-down-right.json`, png: 'nytheris-attack-down-right.png' },
  up: { json: `${NYTHERIS_ASSETS_BASE}/nytheris-attack-up.json`, png: 'nytheris-attack-up.png' },
  'up-left': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-attack-up-left.json`, png: 'nytheris-attack-up-left.png' },
  'up-right': { json: `${NYTHERIS_ASSETS_BASE}/nytheris-attack-up-right.json`, png: 'nytheris-attack-up-right.png' },
} as const

/** Idle + walk + attack atlases all in-folder (attack added; previously reused Sakura's strips). */
export const ARCHERA_ASSETS_BASE = mvmCharacterAnimationsBase('archera')

export const ARCHERA_IDLE_ASSETS = {
  down: { json: `${ARCHERA_ASSETS_BASE}/archera-idle-down.json`, png: 'archera-idle-down.png' },
  'down-left': { json: `${ARCHERA_ASSETS_BASE}/archera-idle-down-left.json`, png: 'archera-idle-down-left.png' },
  'down-right': { json: `${ARCHERA_ASSETS_BASE}/archera-idle-down-right.json`, png: 'archera-idle-down-right.png' },
  up: { json: `${ARCHERA_ASSETS_BASE}/archera-idle-up.json`, png: 'archera-idle-up.png' },
  'up-left': { json: `${ARCHERA_ASSETS_BASE}/archera-idle-up-left.json`, png: 'archera-idle-up-left.png' },
  'up-right': { json: `${ARCHERA_ASSETS_BASE}/archera-idle-up-right.json`, png: 'archera-idle-up-right.png' },
} as const

export const ARCHERA_WALK_ASSETS = {
  down: { json: `${ARCHERA_ASSETS_BASE}/archera-walk-down.json`, png: 'archera-walk-down.png' },
  'down-left': { json: `${ARCHERA_ASSETS_BASE}/archera-walk-down-left.json`, png: 'archera-walk-down-left.png' },
  'down-right': { json: `${ARCHERA_ASSETS_BASE}/archera-walk-down-right.json`, png: 'archera-walk-down-right.png' },
  up: { json: `${ARCHERA_ASSETS_BASE}/archera-walk-up.json`, png: 'archera-walk-up.png' },
  'up-left': { json: `${ARCHERA_ASSETS_BASE}/archera-walk-up-left.json`, png: 'archera-walk-up-left.png' },
  'up-right': { json: `${ARCHERA_ASSETS_BASE}/archera-walk-up-right.json`, png: 'archera-walk-up-right.png' },
} as const

export const ARCHERA_ATTACK_ASSETS = {
  down: { json: `${ARCHERA_ASSETS_BASE}/archera-attack-down.json`, png: 'archera-attack-down.png' },
  'down-left': { json: `${ARCHERA_ASSETS_BASE}/archera-attack-down-left.json`, png: 'archera-attack-down-left.png' },
  'down-right': { json: `${ARCHERA_ASSETS_BASE}/archera-attack-down-right.json`, png: 'archera-attack-down-right.png' },
  up: { json: `${ARCHERA_ASSETS_BASE}/archera-attack-up.json`, png: 'archera-attack-up.png' },
  'up-left': { json: `${ARCHERA_ASSETS_BASE}/archera-attack-up-left.json`, png: 'archera-attack-up-left.png' },
  'up-right': { json: `${ARCHERA_ASSETS_BASE}/archera-attack-up-right.json`, png: 'archera-attack-up-right.png' },
} as const

export const KORTHAS_ASSETS_BASE = mvmCharacterAnimationsBase('korthas')

export const KORTHAS_WALK_ASSETS = {
  down: { json: `${KORTHAS_ASSETS_BASE}/korthas-walk-down.json`, png: 'korthas-walk-down.png' },
  'down-left': { json: `${KORTHAS_ASSETS_BASE}/korthas-walk-down-left.json`, png: 'korthas-walk-down-left.png' },
  'down-right': { json: `${KORTHAS_ASSETS_BASE}/korthas-walk-down-right.json`, png: 'korthas-walk-down-right.png' },
  up: { json: `${KORTHAS_ASSETS_BASE}/korthas-walk-up.json`, png: 'korthas-walk-up.png' },
  'up-left': { json: `${KORTHAS_ASSETS_BASE}/korthas-walk-up-left.json`, png: 'korthas-walk-up-left.png' },
  'up-right': { json: `${KORTHAS_ASSETS_BASE}/korthas-walk-up-right.json`, png: 'korthas-walk-up-right.png' },
} as const

export const KORTHAS_IDLE_ASSETS = {
  down: { json: `${KORTHAS_ASSETS_BASE}/korthas-idle-down.json`, png: 'korthas-idle-down.png' },
  'down-left': { json: `${KORTHAS_ASSETS_BASE}/korthas-idle-down-left.json`, png: 'korthas-idle-down-left.png' },
  'down-right': { json: `${KORTHAS_ASSETS_BASE}/korthas-idle-down-right.json`, png: 'korthas-idle-down-right.png' },
  up: { json: `${KORTHAS_ASSETS_BASE}/korthas-idle-up.json`, png: 'korthas-idle-up.png' },
  'up-left': { json: `${KORTHAS_ASSETS_BASE}/korthas-idle-up-left.json`, png: 'korthas-idle-up-left.png' },
  'up-right': { json: `${KORTHAS_ASSETS_BASE}/korthas-idle-up-right.json`, png: 'korthas-idle-up-right.png' },
} as const

export const KORTHAS_ATTACK_ASSETS = {
  down: { json: `${KORTHAS_ASSETS_BASE}/korthas-attack-down.json`, png: 'korthas-attack-down.png' },
  'down-left': { json: `${KORTHAS_ASSETS_BASE}/korthas-attack-down-left.json`, png: 'korthas-attack-down-left.png' },
  'down-right': { json: `${KORTHAS_ASSETS_BASE}/korthas-attack-down-right.json`, png: 'korthas-attack-down-right.png' },
  up: { json: `${KORTHAS_ASSETS_BASE}/korthas-attack-up.json`, png: 'korthas-attack-up.png' },
  'up-left': { json: `${KORTHAS_ASSETS_BASE}/korthas-attack-up-left.json`, png: 'korthas-attack-up-left.png' },
  'up-right': { json: `${KORTHAS_ASSETS_BASE}/korthas-attack-up-right.json`, png: 'korthas-attack-up-right.png' },
} as const

/* ----------------------------------------------------------------------------------------------
 * Child characters (server `characterOrigin: 'child'`).
 *
 * The 20 roster fighters below each ship a full idle / walk / attack × 6-direction TexturePacker
 * atlas set on the same Blob base as the adults (`animations/mvm-characters/{folder}/`). The id
 * list, canonical spellings and legacy aliases come from the backend source of truth —
 * `childCharacters` in `constants/inventory.js` + `CHILD_CHARACTER_ULTIMATE_SOURCE`
 * (mirrored on the frontend as `mvmChildUltimateResolution.ts`).
 *
 * Resolution is intentionally tolerant so a battle never breaks on id-spelling drift between
 * backend / frontend / asset filenames:
 *   • the incoming id is lower-cased + trimmed,
 *   • known legacy spellings map to the real on-disk folder (CHILD_ID_TO_ASSET_FOLDER),
 *   • any *other* id the backend recognises as a child gets a best-effort standard-naming set.
 *
 * The hex-battle loader reads the PNG from each JSON's `meta.image`, so only the JSON URL has to be
 * exact; `png` here is consumed solely by the service-worker precache. A few atlases shipped with
 * off-pattern filenames (Blob is case-sensitive) and are patched in CHILD_FILE_OVERRIDES.
 * -------------------------------------------------------------------------------------------- */

/** Atlas folders that physically exist under `mvm-characters/` (mirror: `public/assets/characters/`). */
export const CHILD_ASSET_FOLDERS = [
  'astrae', 'aurelyn', 'caladros', 'draxis', 'elowyn', 'elyndra', 'kaedor', 'kaelithra', 'kai',
  'lorcan', 'morvessa', 'nyrelle', 'orienth', 'rexor', 'saelis', 'seraphae', 'silmera', 'tharion',
  'vaelor', 'vireya',
] as const

/**
 * Backend character id → the folder that actually holds its atlases, for ids whose canonical
 * spelling differs from the folder on disk:
 *   • `orineth` is the backend-canonical id; its atlases live in the legacy-spelled `orienth/` folder.
 *   • `enlyndra` is a legacy alias of `elyndra`.
 * Every other child uses its id verbatim as the folder name.
 */
export const CHILD_ID_TO_ASSET_FOLDER: Record<string, string> = {
  orineth: 'orienth',
  enlyndra: 'elyndra',
}

type MvmAnimDir = 'down' | 'down-left' | 'down-right' | 'up' | 'up-left' | 'up-right'
const MVM_ANIM_DIRS: readonly MvmAnimDir[] = ['down', 'down-left', 'down-right', 'up', 'up-left', 'up-right']
type MvmSpriteEntry = { json: string; png: string; sheetBase?: string }
type MvmDirBundle = Record<MvmAnimDir, MvmSpriteEntry>

/**
 * Off-pattern atlas filenames, keyed by FOLDER → `${state}-${dir}` → exact filename stems
 * (no extension/path). `jsonFile` is the on-Blob JSON name; `png` must equal that JSON's
 * `meta.image` so precache fetches the matching image.
 */
const CHILD_FILE_OVERRIDES: Record<string, Partial<Record<string, { jsonFile: string; png: string }>>> = {
  // walk-down shipped capitalized: JSON `Morvessa-walk-down.json` + PNG `Morvessa-walk-down.png`
  // both exist on Blob only in this casing. (The JSON's own meta.image is mis-cased to lowercase and
  // 404s — the loader derives the PNG from the JSON filename instead; precache uses the real name here.)
  morvessa: { 'walk-down': { jsonFile: 'Morvessa-walk-down', png: 'Morvessa-walk-down.png' } },
  // idle atlases in orienth/ use the backend-canonical spelling "orineth"; walk/attack use "orienth".
  orienth: {
    'idle-down': { jsonFile: 'orineth-idle-down', png: 'orineth-idle-down.png' },
    'idle-down-left': { jsonFile: 'orineth-idle-down-left', png: 'orineth-idle-down-left.png' },
    'idle-down-right': { jsonFile: 'orineth-idle-down-right', png: 'orineth-idle-down-right.png' },
    'idle-up': { jsonFile: 'orineth-idle-up', png: 'orineth-idle-up.png' },
    'idle-up-left': { jsonFile: 'orineth-idle-up-left', png: 'orineth-idle-up-left.png' },
    'idle-up-right': { jsonFile: 'orineth-idle-up-right', png: 'orineth-idle-up-right.png' },
  },
}

function makeChildDirBundle(folder: string, base: string, state: 'idle' | 'walk' | 'attack'): MvmDirBundle {
  const overrides = CHILD_FILE_OVERRIDES[folder]
  const entryFor = (dir: MvmAnimDir): MvmSpriteEntry => {
    const ov = overrides?.[`${state}-${dir}`]
    const stem = ov?.jsonFile ?? `${folder}-${state}-${dir}`
    return { json: `${base}/${stem}.json`, png: ov?.png ?? `${folder}-${state}-${dir}.png` }
  }
  return MVM_ANIM_DIRS.reduce((acc, dir) => {
    acc[dir] = entryFor(dir)
    return acc
  }, {} as MvmDirBundle)
}

export type ChildCharacterAssetSet = {
  base: string
  idle: MvmDirBundle
  walk: MvmDirBundle
  attack: MvmDirBundle
}

function buildChildAssetSet(folder: string): ChildCharacterAssetSet {
  const base = mvmCharacterAnimationsBase(folder)
  return {
    base,
    idle: makeChildDirBundle(folder, base, 'idle'),
    walk: makeChildDirBundle(folder, base, 'walk'),
    attack: makeChildDirBundle(folder, base, 'attack'),
  }
}

/** Pre-built atlas sets for every folder that ships sprites (keyed by folder name). */
export const CHILD_CHARACTER_ASSETS: Record<string, ChildCharacterAssetSet> = Object.fromEntries(
  CHILD_ASSET_FOLDERS.map((folder): [string, ChildCharacterAssetSet] => [folder, buildChildAssetSet(folder)]),
)

/** Every id the backend recognises as a child (canonical + legacy spellings), lower-cased. */
const KNOWN_CHILD_IDS: ReadonlySet<string> = new Set(
  Object.keys(CHILD_CHARACTER_ULTIMATE_SOURCE).map((k) => k.toLowerCase()),
)

/**
 * Resolve a (possibly mis-spelled / aliased / wrong-cased) character id to its child atlas set, or
 * `null` if the id is not a child. Tolerant by design — see the section header.
 */
export function getChildCharacterAssetSet(characterId: string): ChildCharacterAssetSet | null {
  const id = (characterId || '').toLowerCase().trim()
  if (!id) return null
  const folder = CHILD_ID_TO_ASSET_FOLDER[id] ?? id
  const explicit = CHILD_CHARACTER_ASSETS[folder]
  if (explicit) return explicit
  // A backend-recognised child we have no explicit folder for: assume the standard naming convention
  // so newly-added children animate without a frontend change (loader falls back gracefully on 404).
  if (KNOWN_CHILD_IDS.has(id)) return buildChildAssetSet(folder)
  return null
}

/** Base URL for resolving spritesheet PNGs next to JSON (sakura default) */
export function getCharacterSpritesheetBaseUrl(characterId: string): string {
  const id = (characterId || 'sakura').toLowerCase()
  const child = getChildCharacterAssetSet(id)
  if (child) return child.base
  if (id === 'rena') return RENA_ASSETS_BASE
  if (id === 'hayate') return HAYATE_ASSETS_BASE
  if (id === 'solvarion') return SOLVARION_ASSETS_BASE
  if (id === 'motoko') return MOTOKO_ASSETS_BASE
  if (id === 'valyra') return VALYRA_ASSETS_BASE
  if (id === 'bruxus') return BRUXUS_ASSETS_BASE
  if (id === 'finza') return FINZA_ASSETS_BASE
  if (id === 'vitalis') return VITALIS_ASSETS_BASE
  if (id === 'bula') return BULA_ASSETS_BASE
  if (id === 'caeloria') return CAELORIA_ASSETS_BASE
  if (id === 'nytheris') return NYTHERIS_ASSETS_BASE
  if (id === 'archera') return ARCHERA_ASSETS_BASE
  if (id === 'korthas') return KORTHAS_ASSETS_BASE
  return SAKURA_ASSETS_BASE
}

export function getCharacterAssets(characterId: string) {
  const id = (characterId || 'sakura').toLowerCase()
  const child = getChildCharacterAssetSet(id)
  if (child) {
    return { idle: child.idle, walk: child.walk, attack: child.attack }
  }
  if (id === 'rena') {
    return { idle: RENA_IDLE_ASSETS, walk: RENA_WALK_ASSETS, attack: RENA_ATTACK_ASSETS }
  }
  if (id === 'hayate') {
    return { idle: HAYATE_IDLE_ASSETS, walk: HAYATE_WALK_ASSETS, attack: HAYATE_ATTACK_ASSETS }
  }
  if (id === 'solvarion') {
    return { idle: SOLVARION_IDLE_ASSETS, walk: SOLVARION_WALK_ASSETS, attack: SOLVARION_ATTACK_ASSETS }
  }
  if (id === 'motoko') {
    return { idle: MOTOKO_IDLE_ASSETS, walk: MOTOKO_WALK_ASSETS, attack: MOTOKO_ATTACK_ASSETS }
  }
  if (id === 'valyra') {
    return { idle: VALYRA_IDLE_ASSETS, walk: VALYRA_WALK_ASSETS, attack: VALYRA_ATTACK_ASSETS }
  }
  if (id === 'bruxus') {
    return { idle: BRUXUS_IDLE_ASSETS, walk: BRUXUS_WALK_ASSETS, attack: BRUXUS_ATTACK_ASSETS }
  }
  if (id === 'finza') {
    return { idle: FINZA_IDLE_ASSETS, walk: FINZA_WALK_ASSETS, attack: FINZA_ATTACK_ASSETS }
  }
  if (id === 'vitalis') {
    return { idle: VITALIS_IDLE_ASSETS, walk: VITALIS_WALK_ASSETS, attack: VITALIS_ATTACK_ASSETS }
  }
  if (id === 'bula') {
    return { idle: BULA_IDLE_ASSETS, walk: BULA_WALK_ASSETS, attack: BULA_ATTACK_ASSETS }
  }
  if (id === 'caeloria') {
    return { idle: CAELORIA_IDLE_ASSETS, walk: CAELORIA_WALK_ASSETS, attack: CAELORIA_ATTACK_ASSETS }
  }
  if (id === 'nytheris') {
    return { idle: NYTHERIS_IDLE_ASSETS, walk: NYTHERIS_WALK_ASSETS, attack: NYTHERIS_ATTACK_ASSETS }
  }
  if (id === 'archera') {
    return { idle: ARCHERA_IDLE_ASSETS, walk: ARCHERA_WALK_ASSETS, attack: ARCHERA_ATTACK_ASSETS }
  }
  if (id === 'korthas') {
    return { idle: KORTHAS_IDLE_ASSETS, walk: KORTHAS_WALK_ASSETS, attack: KORTHAS_ATTACK_ASSETS }
  }
  return { idle: SAKURA_IDLE_ASSETS, walk: SAKURA_WALK_ASSETS, attack: SAKURA_ATTACK_ASSETS }
}

export const BATTLE_BG_URL = `${PUBLIC_URL}/assets/battle/battle-bg.webp`
/** MvM top bar — tiling strip (`background-repeat: repeat-x`). */
export const BATTLE_TOP_BAR_URL = mvmHudBlobUrl('battle-top-bar.webp')
export const BATTLE_TOP_BAR_LEFT_CORNER_URL = `${PUBLIC_URL}/assets/battle/top-bar-corner-left.webp`
export const BATTLE_TOP_BAR_RIGHT_CORNER_URL = `${PUBLIC_URL}/assets/battle/top-bar-corner-right.webp`
/** Top-center plate (turn line). */
export const BATTLE_TOP_BAR_CENTER_PLATE_URL = mvmHudBlobUrl('top-bar-center.webp')
/** @deprecated Use BATTLE_TOP_BAR_CENTER_PLATE_URL */
export const BATTLE_TOP_BAR_TURN_CENTER_URL = BATTLE_TOP_BAR_CENTER_PLATE_URL
/** Surrender — circular icon (`mvm/battle/` on Blob). */
export const MVM_SURRENDER_BTN_BG_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm/battle/surrender-btn.webp'
/** Battle chat — circular icon shown bottom-left, above the surrender button. */
export const MVM_CHAT_ICON_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm/mvm-chat.webp'
/** Tiling strip + corners only; center plate is absolutely positioned and may extend below this. */
export const BATTLE_TOP_BAR_MIN_HEIGHT_PX = 70
/** Extra space under the strip so left HUD clears the hanging center plate art. */
export const BATTLE_TOP_BAR_CENTER_OVERHANG_RESERVE_PX = 44
/** Bottom bar — tiling center strip */
export const BATTLE_BOTTOM_BAR_URL = mvmHudBlobUrl('battle-bottom-bar.webp')
export const BATTLE_BOTTOM_BAR_LEFT_CORNER_URL = `${PUBLIC_URL}/assets/battle/battle-bottom-bar-left-corner.webp`
export const BATTLE_BOTTOM_BAR_RIGHT_CORNER_URL = `${PUBLIC_URL}/assets/battle/battle-bottom-bar-right-corner.webp`
/** Left cluster on bottom bar (round / info). */
export const BATTLE_BOTTOM_INFO_LEFT_URL = mvmHudBlobUrl('bottom-info.webp')
/** Decorative bottom bar min height — keep bottom-floating HUD offsets in `MvMBattleHUD` roughly aligned. */
export const BATTLE_BOTTOM_BAR_MIN_HEIGHT_PX = 72
/** Wallet / turn-order avatar ring — `mvm/battle` (same as `MVM_TURN_ORDER_AVATAR_FRAME_URL` in mvmBattleUiAssets). */
export const MVM_USER_AVATAR_FRAME_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm/battle/character-avatar-frame.webp'
/** Power balance: end caps + four body tiles (2× blue body, 2× red body) in `public/assets/mvmbattle/`. */
export const MVM_PROGRESS_LEFT_CAP_URL = `${PUBLIC_URL}/assets/mvmbattle/progress_left.webp`
export const MVM_PROGRESS_RIGHT_CAP_URL = `${PUBLIC_URL}/assets/mvmbattle/progress_right.webp`
export const MVM_PROGRESS_BLUE_BODY_URL = `${PUBLIC_URL}/assets/mvmbattle/progress_blue_body.webp`
export const MVM_PROGRESS_RED_BODY_URL = `${PUBLIC_URL}/assets/mvmbattle/progress_red_body.webp`
/** End Turn control — `mvm/battle/` on Blob (circular frame art). */
export const MVM_END_TURN_BTN_BG_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm/battle/end-turn-btn-bg.webp'
/** Shown under the End Turn control. */
export const MVM_END_TURN_BELOW_BADGE_URL = mvmHudBlobUrl('end-turn-btn-below-badge.webp')
/** @deprecated alias */
export const MVM_END_TURN_BTN_URL = MVM_END_TURN_BTN_BG_URL
/** Framed character panel (left HUD). */
export const MVM_CHARACTER_PANEL_BG_URL = mvmHudBlobUrl('character-panel-bg.webp')
export const MVM_CHARACTER_PANEL_TOP_URL = mvmHudBlobUrl('character-panel-top.webp')
export const MVM_CHARACTER_PANEL_BOTTOM_URL = mvmHudBlobUrl('character-panel-bottom.webp')
/** Top-center “whose turn” bar — lives under `mvm/battle/` on Blob (not `mvm/new`). */
export const MVM_TOP_TURN_BANNER_BG_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mvm/battle/top-turn-showing-bar-bg.webp'
/** Defender incoming-attack strip + “Request help” art (`GlobalAttackAlert`). */
export const MVM_ATTACK_NOTI_BG_URL = mvmHudBlobUrl('attack-noti-bg.webp')
/** Defender “incoming attack / enter battle” modal — bg + ornamental top & bottom (`GlobalAttackAlert`). */
export const MVM_INCOMING_ATTACK_PANEL_BG_URL = mvmHudBlobUrl('incoming-attack-panel-bg.webp')
export const MVM_INCOMING_ATTACK_PANEL_TOP_URL = mvmHudBlobUrl('incoming-attack-panel-top.webp')
export const MVM_INCOMING_ATTACK_PANEL_BOTTOM_URL = mvmHudBlobUrl('incoming-attack-panel-bottom.webp')

/** MvM battle end modals — art in `public/assets/battle`. */
export const MVM_RESULT_VICTORY_BANNER_BG_URL = mvmHudBlobUrl('victory-banner-bg.webp')
export const MVM_RESULT_DEFEAT_BANNER_BG_URL = mvmHudBlobUrl('defeat-banner-bg.webp')
export const MVM_RESULT_STATUS_PANEL_BG_URL = mvmHudBlobUrl('winning-status-panel-bg.webp')
export const MVM_RESULT_OWNED_ITEM_BG_URL = mvmHudBlobUrl('owned-item-bg.webp')
/** World map — center-left march / rally / return-timer panel. */
export const MVM_WORLD_MAP_ATTACK_STATUS_PANEL_BG_URL = mvmHudBlobUrl('attack-status-panel-bg.webp')

export const MVM_ALLIANCE_BATTLE_PANEL_BG_URL = `${PUBLIC_URL}/assets/mvmbattle/new_alliance_battle_panel_body.webp`
export const MVM_ALLIANCE_BATTLE_PANEL_TOP_URL = `${PUBLIC_URL}/assets/mvmbattle/new_alliance_battle_panel_top.webp`
export const MVM_ALLIANCE_BATTLE_PANEL_BOTTOM_URL = `${PUBLIC_URL}/assets/mvmbattle/new_alliance_battle_panel_bottom.webp`
/** Alliance battle slide panel — title + row icons (`public/assets/mvmbattle`). */
export const MVM_ALLIANCE_BATTLE_ICON_SANDCLOCK_URL = `${PUBLIC_URL}/assets/mvmbattle/icon_sandclock.webp`
export const MVM_ALLIANCE_BATTLE_ICON_POINT_URL = `${PUBLIC_URL}/assets/mvmbattle/icon_point.webp`
/** World map / GlobalAttackAlert — character pick modal header banner (`public/assets/mvmbattle`). */
export const MVM_CHARACTER_SELECT_MODAL_HEADER_BG_URL = `${PUBLIC_URL}/assets/mvmbattle/character_select_card_header.webp`
/** Same modal — footer / action row banner. */
export const MVM_CHARACTER_SELECT_MODAL_BOTTOM_BG_URL = `${PUBLIC_URL}/assets/mvmbattle/character_select_card_bottom.webp`
/** Footer status / controls row (bread, selection summary, confirm) — above the bottom art strip. */
export const MVM_CHARACTER_SELECT_MODAL_CARD_STATUS_BG_URL = `${PUBLIC_URL}/assets/mvmbattle/card_status.webp`
/** Scrollable character grid area between header and footer. */
export const MVM_CHARACTER_SELECT_MODAL_BODY_BG_URL = `${PUBLIC_URL}/assets/mvmbattle/character_select_card_body.webp`
/** Primary action on character-select modal (`public/assets/mvmbattle`). */
export const MVM_CHARACTER_SELECT_BTN_ATTACK_URL = `${PUBLIC_URL}/assets/mvmbattle/btn_attack.webp`
/**
 * Fixed shell height for world-map marching attacks + alliance battle slide panels.
 * Shorter default; parchment body scrolls when content overflows.
 */
export const MVM_SLIDE_PANEL_SHELL_HEIGHT = 'max(220px, min(28vh, 300px))' as const
/**
 * Marching + alliance slide panels on world map in phone/tablet landscape (`isWorldMapLandscapeCompact`).
 * Inner parchment still scrolls when content overflows.
 */
export const MVM_SLIDE_PANEL_SHELL_HEIGHT_LANDSCAPE_COMPACT =
  'max(142px, min(18vh, 200px))' as const
/** Fixed body height for alliance roster panel frame; inner lists scroll (3/5 of prior 420px). */
export const MVM_ALLIANCE_BATTLE_PANEL_HEIGHT_PX = 252
/** Decorative top/bottom strip height (background stretched 100% × 100%). */
export const MVM_ALLIANCE_BATTLE_PANEL_DECOR_H_PX = 44
/** Pull strips slightly past the panel edge so they overlap the main background. */
export const MVM_ALLIANCE_BATTLE_PANEL_DECOR_OUTSET_PX = 12
/**
 * Inner top padding for the parchment scroll area on alliance battle + marching attacks slide panels.
 * Keeps the first row off the header seam without crowding the fixed shell height.
 */
export const MVM_SLIDE_PANEL_BODY_PADDING_TOP_PX = 14
/** Landscape-compact world-map slide panels — tighter parchment inset + typography budget. */
export const MVM_SLIDE_PANEL_BODY_PADDING_TOP_COMPACT_PX = 10
/** Extra upward offset for top strip only (moves `panel-top` closer to screen top). */
export const MVM_ALLIANCE_BATTLE_PANEL_TOP_EXTRA_UP_PX = 16
/** Character panel stat row icons. */
export const MVM_HUD_ICON_ATTACK_URL = mvmHudBlobUrl('attack.webp')
export const MVM_HUD_ICON_ARMOR_URL = mvmHudBlobUrl('armor.webp')
export const MVM_HUD_ICON_MOVE_URL = mvmHudBlobUrl('move.webp')
export const MVM_HUD_ICON_POWER_URL = mvmHudBlobUrl('power.webp')
export const MVM_HUD_ICON_HP_URL = mvmHudBlobUrl('hp.webp')
export const MVM_HUD_ICON_MORALE_URL = mvmHudBlobUrl('morale.webp')
export const MVM_HUD_ICON_RANGE_URL = mvmHudBlobUrl('range.webp')
export const BATTLE_MAP_ASSETS_URL = `${PUBLIC_URL}/assets/battle/battle-map-assets.json`
export const BATTLE_MAP_ASSETS_URLS = [
  `${PUBLIC_URL}/assets/battle/battle-map-assets.json`,
  `${PUBLIC_URL}/assets/battle/battle-map-assets-2.json`,
]
/** Atlas: `battle-map-assets-3.json` + `battle-map-assets-3.png` — sprites placed at each frame’s `x,y` in JSON (pixiInit `layoutMap3BattleArt`). */
export const BATTLE_MAP_ASSETS_URL_MAP3 = `${PUBLIC_URL}/assets/battle/battle-map-assets-3.json`
export const BATTLE_MAP_URL = `${PUBLIC_URL}/assets/battle/battle_map.json`
/** Bump when `basic-battle-assets.png` / `.json` change so browsers refetch (see utils loadSpritesheetFrame). */
export const BASIC_BATTLE_ASSETS_CACHE_QUERY = '?v=4'

/** Basic battle assets spritesheet (hex highlight tiles + barrack + tower); frames are `.png` keys in JSON. */
export const BASIC_BATTLE_ASSETS_URL = `${PUBLIC_URL}/assets/battle/basic-battle-assets.json${BASIC_BATTLE_ASSETS_CACHE_QUERY}`
export const BASIC_BATTLE_ASSETS_BASE = `${PUBLIC_URL}/assets/battle`

/**
 * Watchtower art in basic-battle-assets.png (see basic-battle-assets.json `frames`).
 * Attacker = barrack, defender = tower (spritesheet may mark frames `rotated`; PIXI uprights them).
 */
export const WATCHTOWER_SPRITESHEET_FRAMES = {
  attacker: 'barrack.png',
  defender: 'tower.png',
} as const

/**
 * Per-type tower art atlas (`public/assets/battle/tower_asset.json` / `.png`). Six frames:
 * `{heal,support,supply}_tower.png` (defender side) and `{heal,support,supply}_tent.png` (attacker side).
 */
/** Bump when `tower_asset.png` / `.json` change so browsers/CDN refetch (applied to both JSON + image URL). */
export const TOWER_ASSET_CACHE_QUERY = '?v=2'
export const TOWER_ASSET_URL = `${PUBLIC_URL}/assets/battle/tower_asset.json${TOWER_ASSET_CACHE_QUERY}`

/**
 * Nudge tower container vs hex center after grid→view (px). Tune when atlas layout/foot changes.
 * Atlas (`public/assets/battle/basic-battle-assets.json`): sheet 222×755; hex highlights top; tower 110×175; barrack 142×112 rotated — see `frames` in JSON.
 */
export const WATCHTOWER_HEX_NUDGE = {
  attacker: { x: 0, y: 8 },
  defender: { x: 2, y: 11 },
} as const
export const HEX_PADDING = 2.5
/**
 * Cap PIXI canvas backing-store scale for the hex battle screen.
 * High DPR (2–3×) multiplies fragment load; integrated / software WebGL benefits from ~1.25–1.5×.
 */
export const HEX_BATTLE_MAX_DEVICE_PIXEL_RATIO = 1.35

/** Battlefield view: rotated 40° for "looking from afar" perspective */
export const BATTLEFIELD_ROTATION = (30 * Math.PI) / 180
/** Slight scale-down to enhance "viewing from afar" feel */
export const BATTLEFIELD_SCALE = 0.88

export const MOVE_RANGE = 0
/** Seconds per hex when moving along path */
export const MOVE_DURATION_PER_HEX = 0.25
/** Seconds for one full walk animation cycle (all frames) */
export const WALK_ANIMATION_DURATION = 0.5
/** Seconds for one full idle animation cycle (all frames) */
export const IDLE_ANIMATION_DURATION = 1
/** Duration of attack animation when clicking enemy hex */
export const ATTACK_ANIMATION_DURATION = 0.4
export const PLAYER_MAX_HP = 100
export const GRID_WIDTH = 35
export const GRID_HEIGHT = 40
/** ~60px tile view (flat-to-flat) at zoom 1; doubled for visibility */
export const HEX_RADIUS = 32
/** Pointy-top hex: width = √3·R, height adjusted for 7:9 ratio */
export const HEX_WIDTH = Math.sqrt(3) * HEX_RADIUS
export const HEX_HEIGHT = HEX_WIDTH * (3 / 2)

/** Hexagon aspect / world Y-scale */
export const HEX_ASPECT_Y = 2 / 3

/** Life (healing) tower heal radius, in hex steps. MUST match backend MVM_LIFE_TOWER_HEAL_RANGE. */
export const MVM_LIFE_TOWER_HEAL_RANGE = 3
/** Opacity of the Life-tower heal-range overlay (hexagon-ready tiles). */
export const MVM_LIFE_TOWER_RANGE_HEX_ALPHA = 0.3

/**
 * On-screen height (world px, before camera zoom) every character's **body** should render at —
 * measured feet-to-head, NOT the raw sprite frame. Frames vary wildly (idle ~105px, attack frames
 * 175–230px once you include sword/effect overhang), so scaling by frame height shrinks the body
 * inconsistently. The renderer instead measures the real body from the idle pose's trim box and
 * scales every state to this target. See `measureSpriteBodyMetrics` / `computeCharacterBaseScale`
 * in `./utils` and the call sites in `pixiInit.spawnCharacterSprite`.
 */
export const CHARACTER_BODY_TARGET_PX = 100

/**
 * Per-character render escape hatch — intentionally empty. The body-fit algorithm is fully generic
 * (idle-trim-derived scale + trim-derived feet anchor), so no character needs hand-tuning. If a
 * specific atlas is ever drawn off-spec (e.g. a tall halo in the idle pose inflating its measured
 * body, or a ground effect dragging the feet anchor down), add a minimal correction here rather
 * than re-introducing per-character branches in the renderer.
 *   • bodyScaleMul   — multiply the computed body scale (e.g. 1.1 = 10% taller).
 *   • feetAnchorBias — add to the 0..1 feet anchor (e.g. -0.05 lifts the character off the tile).
 */
export const CHARACTER_RENDER_TUNING: Record<string, { bodyScaleMul?: number; feetAnchorBias?: number }> = {}
